import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import LeftSidebar from './components/LeftSidebar';
import RightContent from './components/RightContent';
import TimePeriodDropdown from './components/TimePeriodDropdown';
import DateRangeDisplay from './components/DateRangeDisplay';
import { GET_TIME_DISTRIBUTION_BY_SKILLS, GET_EMPLOYEE_GOALS_BY_SKILL, GET_EMPLOYEE_PROJECTS_BY_SKILL, GET_EMPLOYEE_TEAM_MEMBERS, USER_EMPLOYEE_BY_EMPLOYEE_ID, EMPLOYEES_BY_AGENCY_ID, GET_EMPLOYEE_TIME_DISTRIBUTION_BY_CLIMB_TYPE, GET_EMPLOYEE_GOALS_BY_CLIMB_TYPE, GET_EMPLOYEE_PROJECTS_BY_CLIMB_TYPE, GET_EMPLOYEE_PEER_POINTS_FOR_DATE_RANGE } from '../../api/query';
import { useAuth } from '../../context/AuthContext';
import { getDateRange, isFuturePeriod } from './utils/dateUtils';

interface SkillData {
    skill_id: number | null;
    skill_name: string;
    task_count: number;
    estimated_hours: number;
    actual_hours: number;
}

interface TimeDistributionData {
    status: string;
    message: string;
    status_code: number;
    skill_groups: SkillData[];
}

const COLORS = ['#2398ff', '#8231a7', '#33ad0b', '#f2a217', '#e74c3c', '#249dde'];

const UserProfile = () => {
    const { userInfo } = useAuth()
    // const navigate = useNavigate();
    const loggedInEmployeeID = userInfo?.employee_id || '';
    const organizationID = userInfo?.agencies?.id || '';

    const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>(loggedInEmployeeID);

    const [selectedPeriod, setSelectedPeriod] = useState<string>('Month to date');
    const [viewMode, setViewMode] = useState<'skills' | 'climb'>('skills'); // Toggle between Skills and CLIMB
    const [selectedSkill, setSelectedSkill] = useState<string | null>(null);
    const [selectedClimbType, setSelectedClimbType] = useState<string | null>(null);
    const [goalsPage, setGoalsPage] = useState<number>(1);
    const [projectsPage, setProjectsPage] = useState<number>(1);

    // Update selectedEmployeeId when userInfo loads
    useEffect(() => {
        if (loggedInEmployeeID && !selectedEmployeeId) {
            setSelectedEmployeeId(loggedInEmployeeID);
        }
    }, [loggedInEmployeeID, selectedEmployeeId]);

    // Get date range based on selected period
    const { startDate, endDate } = getDateRange(selectedPeriod);
    
    // Determine if we should use estimated_hours or actual_hours
    const useFutureTime = isFuturePeriod(selectedPeriod);

    // Fetch all employees for dropdown
    const { loading: employeesLoading, data: employeesData } = useQuery(EMPLOYEES_BY_AGENCY_ID, {
        variables: {
            agency_id: parseInt(organizationID)
        },
        skip: !organizationID,
        fetchPolicy: 'no-cache'
    });

    // Fetch employee data for selected employee
    const { loading: employeeLoading, error: employeeError, data: employeeData } = useQuery(USER_EMPLOYEE_BY_EMPLOYEE_ID, {
        variables: {
            employee_id: parseInt(selectedEmployeeId)
        },
        skip: !selectedEmployeeId,
        fetchPolicy: 'no-cache'
    });

    // Fetch time distribution data
    const { loading: timeDistributionLoading, error: timeDistributionError, data } = useQuery(GET_TIME_DISTRIBUTION_BY_SKILLS, {
        variables: {
            employee_id: parseInt(selectedEmployeeId),
            start_date: startDate,
            end_date: endDate,
        },
        skip: !selectedEmployeeId,
        fetchPolicy: 'no-cache'
    });

    const timeDistributionData: TimeDistributionData | null = data?.get_employee_time_distribution_by_skills || null;

    // Fetch CLIMB time distribution data
    const { loading: climbDistributionLoading, error: climbDistributionError, data: climbData } = useQuery(GET_EMPLOYEE_TIME_DISTRIBUTION_BY_CLIMB_TYPE, {
        variables: {
            employee_id: parseInt(selectedEmployeeId),
            start_date: startDate,
            end_date: endDate,
        },
        skip: !selectedEmployeeId,
        fetchPolicy: 'no-cache'
    });

    const climbDistributionData = climbData?.get_employee_time_distribution_by_climb_type || null;

    // Get skill_id from selected skill name
    const getSkillIdFromName = (skillName: string | null) => {
        if (!skillName || !timeDistributionData?.skill_groups) return null;
        const skill = timeDistributionData.skill_groups.find(s => s.skill_name === skillName);
        return skill?.skill_id || null;
    };

    const selectedSkillId = getSkillIdFromName(selectedSkill);

    // Reset pages when skill or climb type changes
    useEffect(() => {
        setGoalsPage(1);
        setProjectsPage(1);
    }, [selectedSkill, selectedClimbType]);

    // Reset selections when period changes
    useEffect(() => {
        setSelectedSkill(null);
        setSelectedClimbType(null);
        setGoalsPage(1);
        setProjectsPage(1);
    }, [selectedPeriod]);

    // Fetch goals by skill
    const { loading: goalsLoading, error: goalsError, data: goalsData } = useQuery(GET_EMPLOYEE_GOALS_BY_SKILL, {
        variables: {
            employee_id: parseInt(selectedEmployeeId),
            start_date: startDate,
            end_date: endDate,
            skill_id: selectedSkillId,
            page: goalsPage,
            page_size: 5
        },
        skip: !selectedEmployeeId,
        fetchPolicy: 'no-cache'
    });

    // Fetch projects by skill
    const { loading: projectsLoading, error: projectsError, data: projectsData } = useQuery(GET_EMPLOYEE_PROJECTS_BY_SKILL, {
        variables: {
            employee_id: parseInt(selectedEmployeeId),
            start_date: startDate,
            end_date: endDate,
            skill_id: selectedSkillId,
            page: projectsPage,
            page_size: 5
        },
        skip: !selectedEmployeeId,
        fetchPolicy: 'no-cache'
    });

    // Fetch goals by CLIMB type
    const { loading: climbGoalsLoading, error: climbGoalsError, data: climbGoalsData } = useQuery(GET_EMPLOYEE_GOALS_BY_CLIMB_TYPE, {
        variables: {
            employee_id: parseInt(selectedEmployeeId),
            start_date: startDate,
            end_date: endDate,
            climb_type: selectedClimbType,
            page: goalsPage,
            page_size: 5
        },
        skip: !selectedEmployeeId,
        fetchPolicy: 'no-cache'
    });

    // Fetch projects by CLIMB type
    const { loading: climbProjectsLoading, error: climbProjectsError, data: climbProjectsData } = useQuery(GET_EMPLOYEE_PROJECTS_BY_CLIMB_TYPE, {
        variables: {
            employee_id: parseInt(selectedEmployeeId),
            start_date: startDate,
            end_date: endDate,
            climb_type: selectedClimbType,
            page: projectsPage,
            page_size: 5
        },
        skip: !selectedEmployeeId,
        fetchPolicy: 'no-cache'
    });

    // Fetch team members
    const { loading: teamMembersLoading, error: teamMembersError, data: teamMembersData } = useQuery(GET_EMPLOYEE_TEAM_MEMBERS, {
        variables: {
            employee_id: parseInt(selectedEmployeeId),
            start_date: startDate,
            end_date: endDate
        },
        skip: !selectedEmployeeId,
        fetchPolicy: 'no-cache'
    });

    // Fetch peer points
    const { loading: peerPointsLoading, error: peerPointsError, data: peerPointsData } = useQuery(GET_EMPLOYEE_PEER_POINTS_FOR_DATE_RANGE, {
        variables: {
            employee_id: parseInt(selectedEmployeeId),
            start_date: startDate,
            end_date: endDate
        },
        skip: !selectedEmployeeId,
        fetchPolicy: 'no-cache'
    });

    // Transform employees list for dropdown
    const employeeOptions = useMemo(() => {
        if (!employeesData?.employees_by_agency_id?.employees) return [];
        return employeesData.employees_by_agency_id.employees.map((emp: { employee_id: number; first_name: string; last_name: string }) => ({
            key: emp.employee_id,
            name: `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || 'Employee'
        }));
    }, [employeesData]);

    // Transform employee data from API
    const employeeProfile = useMemo(() => {
        const employee = employeeData?.employee_by_employee_id?.employees?.[0];
        if (!employee) {
            return {
                name: 'Employee',
                designation: undefined,
                imageUrl: undefined
            };
        }
        const firstName = employee.first_name || '';
        const lastName = employee.last_name || '';
        return {
            name: `${firstName} ${lastName}`.trim() || 'Employee',
            designation: employee.designation,
            imageUrl: employee.image_url
        };
    }, [employeeData]);

    // Transform skills data from API
    const skills = useMemo(() => {
        const employee = employeeData?.employee_by_employee_id?.employees?.[0];
        if (!employee?.skills) return [];
        return employee.skills.map((skill: any) => ({
            id: skill.id,
            skill_id: skill.skill_id,
            skill_name: skill.skill_name,
            proficiency_level: skill.proficiency_level,
            years_of_experience: skill.years_of_experience,
            certified: skill.certified
        }));
    }, [employeeData]);

    // Transform data for skills pie chart
    const chartData = useMemo(() => {
        if (!timeDistributionData?.skill_groups) return [];
        return timeDistributionData.skill_groups.map(skill => {
            const hours = useFutureTime ? skill.estimated_hours : skill.actual_hours;
            return {
                name: skill.skill_name,
                value: hours,
                hours: hours
            };
        });
    }, [timeDistributionData, useFutureTime]);

    // Transform data for CLIMB pie chart
    const climbChartData = useMemo(() => {
        if (!climbDistributionData?.climb_type_groups) return [];
        return climbDistributionData.climb_type_groups.map((climb: any) => {
            const hours = useFutureTime ? climb.estimated_hours : climb.actual_hours;
            return {
                name: climb.climb_type,
                value: hours,
                hours: hours
            };
        });
    }, [climbDistributionData, useFutureTime]);

    // Transform API goals data
    const goals = useMemo(() => {
        if (!goalsData?.get_employee_goals_by_skill?.goals) return [];
        return goalsData.get_employee_goals_by_skill.goals.map((goal: any) => ({
            goal_id: goal.goal_id,
            goal_name: goal.goal_name,
            goal_status: goal.goal_status,
            end_date: goal.end_date,
            employee_task_count: goal.employee_task_count,
            employee_actual_hours: useFutureTime ? goal.employee_estimated_hours : goal.employee_actual_hours,
            skill_name: goal.skill_name
        }));
    }, [goalsData, useFutureTime]);

    // Transform API projects data
    const projects = useMemo(() => {
        if (!projectsData?.get_employee_projects_by_skill?.projects) return [];
        return projectsData.get_employee_projects_by_skill.projects.map((project: any) => ({
            project_id: project.project_id,
            project_name: project.project_name,
            skill_name: project.skill_name,
            employee_goal_count: project.employee_goal_count,
            employee_actual_hours: useFutureTime ? project.employee_estimated_hours : project.employee_actual_hours
        }));
    }, [projectsData, useFutureTime]);

    // Transform CLIMB goals data
    const climbGoals = useMemo(() => {
        if (!climbGoalsData?.get_employee_goals_by_climb_type?.goals) return [];
        return climbGoalsData.get_employee_goals_by_climb_type.goals.map((goal: any) => ({
            goal_id: goal.goal_id,
            goal_name: goal.goal_name,
            goal_status: goal.goal_status,
            end_date: goal.end_date,
            employee_task_count: goal.employee_task_count,
            employee_actual_hours: useFutureTime ? goal.employee_estimated_hours : goal.employee_actual_hours,
            climb_type: goal.climb_type
        }));
    }, [climbGoalsData, useFutureTime]);

    // Transform CLIMB projects data
    const climbProjects = useMemo(() => {
        if (!climbProjectsData?.get_employee_projects_by_climb_type?.projects) return [];
        return climbProjectsData.get_employee_projects_by_climb_type.projects.map((project: any) => ({
            project_id: project.project_id,
            project_name: project.project_name,
            climb_type: project.climb_type,
            employee_goal_count: project.employee_goal_count,
            employee_actual_hours: useFutureTime ? project.employee_estimated_hours : project.employee_actual_hours
        }));
    }, [climbProjectsData, useFutureTime]);

    // Transform API team members data
    const teamMembers = useMemo(() => {
        if (!teamMembersData?.get_employee_team_members?.team_members) return [];
        return teamMembersData.get_employee_team_members.team_members.map((member: any) => ({
            employee_id: member.employee_id,
            employee_name: member.employee_name,
            shared_goals_count: member.shared_goals_count
        }));
    }, [teamMembersData]);

    // Transform API peer points data
    const peerPoints = useMemo(() => {
        const summary = peerPointsData?.get_employee_peer_points_for_date_range?.summary;
        if (!summary) {
            return {
                received: 0,
                given: 0,
                monthlyBreakdown: []
            };
        }
        return {
            received: summary.total_points_earned || 0,
            given: summary.total_points_given || 0,
            monthlyBreakdown: summary.monthly_breakdown || []
        };
    }, [peerPointsData]);

    // Get pagination data from API
    const goalsPagination = goalsData?.get_employee_goals_by_skill?.pagination || null;
    const projectsPagination = projectsData?.get_employee_projects_by_skill?.pagination || null;
    const climbGoalsPagination = climbGoalsData?.get_employee_goals_by_climb_type?.pagination || null;
    const climbProjectsPagination = climbProjectsData?.get_employee_projects_by_climb_type?.pagination || null;

    // Handle employee selection change
    const handleEmployeeSelect = (employeeId: number, employeeName: string) => {
        setSelectedEmployeeId(employeeId.toString());
        // Reset filters when changing employee
        setSelectedSkill(null);
        setSelectedClimbType(null);
        setGoalsPage(1);
        setProjectsPage(1);
    };

    // Handle view mode toggle
    const handleViewModeToggle = () => {
        const newMode = viewMode === 'skills' ? 'climb' : 'skills';
        setViewMode(newMode);
        // Reset selections when switching modes
        setSelectedSkill(null);
        setSelectedClimbType(null);
        setGoalsPage(1);
        setProjectsPage(1);
    };

    // Get initial selected employee name for dropdown
    const selectedEmployeeName = useMemo(() => {
        const selected = employeeOptions.find((emp: { key: number; name: string }) => emp.key === parseInt(selectedEmployeeId));
        return selected?.name || employeeProfile.name;
    }, [employeeOptions, selectedEmployeeId, employeeProfile.name]);

    return (
        <div id="cts-respon-p-dv" className="w-full relative overflow-hidden flex flex-row items-start justify-center flex-wrap content-start py-[100px] px-20 mq991:p-8 mq575:p-4 box-border text-left text-base text-text-primary font-manrope">
            <div className="w-full flex flex-col gap-6">
                {/* Filter Buttons and View Toggle */}


                {/* Main Content */}
                <div className="flex flex-row  mq991:flex-col leftsidebar-userprofile">
                    <LeftSidebar
                        employeeName={employeeProfile.name}
                        designation={employeeProfile.designation}
                        imageUrl={employeeProfile.imageUrl}
                        employeeLoading={employeeLoading}
                        employeeError={employeeError}
                        skills={skills}
                        skillsLoading={employeeLoading}
                        skillsError={employeeError}
                        teamMembers={teamMembers}
                        teamMembersLoading={teamMembersLoading}
                        teamMembersError={teamMembersError}
                        peerPoints={peerPoints}
                        peerPointsLoading={peerPointsLoading}
                        peerPointsError={peerPointsError}
                        employeeOptions={employeeOptions}
                        selectedEmployeeName={selectedEmployeeName}
                        onEmployeeSelect={handleEmployeeSelect}
                        employeesLoading={employeesLoading}
                    />
                    <div className="block flex-row gap-6 mq991:flex-col w-full user-profile-main-blg p-5">
                        <div className="flex justify-between items-center flex-wrap gap-4 mb-6">
                            <div className="flex items-center gap-4 mq991:flex-wrap">
                                <h3 className=" m-0 flex-1 relative text-inherit font-bold font-inherit max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">
                                    Time Distribution by {viewMode === 'skills' ? 'Skills' : 'CLIMB'}
                                </h3>
                                {/* View Mode Toggle */}
                                <button
                                    onClick={handleViewModeToggle}
                                    className="px-4 py-2 rounded-lg text-sm bg-main-blue text-white hover:bg-blue-600 transition-colors user-profile-leftbtn flex items-center gap-[8px]"
                                >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 1024 1024"><path fill="#000000" d="M118.656 438.656a32 32 0 0 1 0-45.248L416 96l4.48-3.776A32 32 0 0 1 461.248 96l3.712 4.48a32.064 32.064 0 0 1-3.712 40.832L218.56 384H928a32 32 0 1 1 0 64H141.248a32 32 0 0 1-22.592-9.344zM64 608a32 32 0 0 1 32-32h786.752a32 32 0 0 1 22.656 54.592L608 928l-4.48 3.776a32.064 32.064 0 0 1-40.832-49.024L805.632 640H96a32 32 0 0 1-32-32z"/></svg>    
                                    <span className="font-semibold">Switch to {viewMode === 'skills' ? 'CLIMB' : 'Skills'}</span>
                                </button>
                            </div>
                            <div className="flex flex-col items-end gap-2 userprofile-rightbtn">
                                <TimePeriodDropdown
                                    selectedPeriod={selectedPeriod}
                                    onPeriodChange={setSelectedPeriod}
                                />
                                <DateRangeDisplay startDate={startDate} endDate={endDate} />
                            </div>
                        </div>
                        <RightContent
                            viewMode={viewMode}
                            chartData={viewMode === 'skills' ? chartData : climbChartData}
                            colors={COLORS}
                            timeDistributionLoading={viewMode === 'skills' ? timeDistributionLoading : climbDistributionLoading}
                            timeDistributionError={viewMode === 'skills' ? timeDistributionError : climbDistributionError}
                            projects={viewMode === 'skills' ? projects : climbProjects}
                            projectsLoading={viewMode === 'skills' ? projectsLoading : climbProjectsLoading}
                            projectsError={viewMode === 'skills' ? projectsError : climbProjectsError}
                            projectsPagination={viewMode === 'skills' ? projectsPagination : climbProjectsPagination}
                            projectsPage={projectsPage}
                            onProjectsPageChange={setProjectsPage}
                            goals={viewMode === 'skills' ? goals : climbGoals}
                            goalsLoading={viewMode === 'skills' ? goalsLoading : climbGoalsLoading}
                            goalsError={viewMode === 'skills' ? goalsError : climbGoalsError}
                            goalsPagination={viewMode === 'skills' ? goalsPagination : climbGoalsPagination}
                            goalsPage={goalsPage}
                            onGoalsPageChange={setGoalsPage}
                            selectedSkill={selectedSkill}
                            onSkillSelect={setSelectedSkill}
                            selectedClimbType={selectedClimbType}
                            onClimbTypeSelect={setSelectedClimbType}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserProfile;
